import { useState, useMemo } from "react";
import { useApiUrl, useCustom, useList } from "@refinedev/core";
import * as d3 from "d3";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import {
  Anchor,
  Badge,
  Button,
  useMantineTheme,
  Flex,
  Grid,
  Group,
  Loader,
  Paper,
  RingProgress,
  Select,
  Table,
  Text,
  Title,
  Tooltip,
  ActionIcon,
} from "@mantine/core";
import { DatePicker } from "@mantine/dates";
import { Link, useNavigate } from "react-router-dom";
import { openConfirmModal } from "@mantine/modals";
import axiosInstance from "../../axiosInstance";
import { IconInfoCircle, IconRefresh } from "@tabler/icons-react";
import { selectBrands } from "entities/cancel_flow/brand";
import { colorsByOfferType } from "entities/cancel_flow/offerType";
import type { CancelPerson } from "entities/cancel_flow";
import { StatCard } from "./StatCard";

const REASON_COLORS = [
  "#ff6b6b",
  "#fcc419",
  "#51cf66",
  "#339af0",
  "#66d9c8",
  "#343a40",
  "#e64980",
  "#ff922b",
  "#3b5bdb",
  "#c0eb75",
  "#845ef7",
  "#20c997",
  "#fd7e14",
  "#868e96",
  "#f06595",
];

type SummaryData = {
  total: number;
  cancelled: number;
  deflected: number;
  abandoned: number;
  in_progress: number;
  deflection_rate: number;
  date_from: string;
  date_to: string;
};

type ReasonBreakdownItem = {
  reason: string;
  count: number;
};

type OfferBreakdownItem = {
  offer_type: string;
  offer_value: string;
  shown: number;
  accepted: number;
  acceptance_rate: number;
  saved: number;
  lost: number;
  watching: number;
  save_rate: number;
  saved_revenue: number;
};

type OffersBreakdownData = {
  total_shown: number;
  total_accepted: number;
  acceptance_rate: number;
  by_type: {
    offer_type: string;
    shown: number;
    accepted: number;
  }[];
  by_offer: OfferBreakdownItem[];
};

type WatchlistSummaryData = {
  watching: number;
  saved: number;
  lost: number;
  overdue: number;
};

type SavePerformancePoint = {
  week: string;
  total: number;
  deflected: number;
  cancelled: number;
  saves: number;
  deflect_rate: number;
  save_rate: number;
};

type SavePerformanceData = {
  data: SavePerformancePoint[];
  overall_deflect_rate: number;
  overall_save_rate: number;
};

function CancelReasonsPieChart({
  reasons,
  totalReasons,
  onReasonClick,
}: {
  reasons: ReasonBreakdownItem[];
  totalReasons: number;
  onReasonClick?: () => void;
}) {
  const theme = useMantineTheme();
  const dark = theme.colorScheme === "dark";
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);

  const size = 180;
  const radius = size / 2;
  const innerRadius = 0;

  const pieGenerator = useMemo(
    () =>
      d3
        .pie<ReasonBreakdownItem>()
        .value((d) => d.count)
        .sort(null),
    [],
  );
  const arcGenerator = useMemo(
    () =>
      d3
        .arc<d3.PieArcDatum<ReasonBreakdownItem>>()
        .innerRadius(innerRadius)
        .outerRadius(radius),
    [radius],
  );
  const hoverArcGenerator = useMemo(
    () =>
      d3
        .arc<d3.PieArcDatum<ReasonBreakdownItem>>()
        .innerRadius(innerRadius)
        .outerRadius(radius + 6),
    [radius],
  );
  const labelArcGenerator = useMemo(
    () =>
      d3
        .arc<d3.PieArcDatum<ReasonBreakdownItem>>()
        .innerRadius(radius * 0.5)
        .outerRadius(radius * 0.85),
    [radius],
  );

  const arcs = useMemo(() => pieGenerator(reasons), [pieGenerator, reasons]);

  return (
    <Flex direction="column" gap="md">
      <Flex justify="center">
        <svg
          width={size + 12}
          height={size + 12}
          style={{ overflow: "visible" }}
        >
          <g transform={`translate(${(size + 12) / 2}, ${(size + 12) / 2})`}>
            {arcs.map((arc, i) => (
              <path
                key={reasons[i].reason}
                d={
                  (hoveredIndex === i ? hoverArcGenerator : arcGenerator)(
                    arc,
                  ) || ""
                }
                fill={REASON_COLORS[i % REASON_COLORS.length]}
                stroke={dark ? theme.colors.dark[7] : "white"}
                strokeWidth={2}
                style={{ cursor: "default", transition: "d 0.2s ease" }}
                onMouseEnter={() => setHoveredIndex(i)}
                onMouseLeave={() => setHoveredIndex(null)}
              />
            ))}
            {hoveredIndex !== null &&
              (() => {
                const arc = arcs[hoveredIndex];
                const pct =
                  totalReasons > 0
                    ? (
                        (reasons[hoveredIndex].count / totalReasons) *
                        100
                      ).toFixed(0)
                    : "0";
                const [cx, cy] = labelArcGenerator.centroid(arc);
                return (
                  <text
                    x={cx}
                    y={cy}
                    textAnchor="middle"
                    dominantBaseline="central"
                    fontSize={13}
                    fontWeight={700}
                    fill="white"
                    style={{
                      pointerEvents: "none",
                      textShadow: "0 1px 3px rgba(0,0,0,0.4)",
                    }}
                  >
                    {pct}%
                  </text>
                );
              })()}
          </g>
        </svg>
      </Flex>

      <Text size="xs" color="dimmed" fw={700} tt="uppercase">
        Cancel reasons
      </Text>
      <Flex direction="column" gap={4}>
        {reasons.map((item, i) => {
          const pct =
            totalReasons > 0
              ? ((item.count / totalReasons) * 100).toFixed(1)
              : "0";
          return (
            <Flex
              key={item.reason}
              align="center"
              gap="xs"
              py={4}
              px="xs"
              sx={(theme) => ({
                borderRadius: theme.radius.sm,
                backgroundColor:
                  hoveredIndex === i ? theme.colors.gray[1] : "transparent",
                transition: "background-color 0.15s ease",
                cursor: onReasonClick ? "pointer" : "default",
              })}
              onMouseEnter={() => setHoveredIndex(i)}
              onMouseLeave={() => setHoveredIndex(null)}
              onClick={onReasonClick}
            >
              <div
                style={{
                  width: 10,
                  height: 10,
                  borderRadius: "50%",
                  backgroundColor: REASON_COLORS[i % REASON_COLORS.length],
                  flexShrink: 0,
                }}
              />
              <Text size="sm" sx={{ flex: 1 }}>
                {item.reason}
              </Text>
              <Text size="sm" fw={700}>
                {item.count.toLocaleString()}
              </Text>
              <Text size="sm" color="dimmed" fw={600}>
                {pct}%
              </Text>
            </Flex>
          );
        })}
      </Flex>
    </Flex>
  );
}

export const CancelFlowDashboard = () => {
  const apiUrl = useApiUrl();
  const navigate = useNavigate();
  const [brand, setBrand] = useState<string | null>(null);
  const [dateFrom, setDateFrom] = useState<Date | null>(
    new Date(Date.now() - 30 * 24 * 60 * 60 * 1000),
  );
  const [dateTo, setDateTo] = useState<Date | null>(new Date());
  const [resolvingWatchlist, setResolvingWatchlist] = useState(false);
  const [resolvingSessions, setResolvingSessions] = useState(false);

  const endOfDay = (d: Date) => {
    const end = new Date(d);
    end.setHours(23, 59, 59, 999);
    return end;
  };

  const queryParams: Record<string, string> = {};
  if (brand) queryParams.brand = brand;
  if (dateFrom) queryParams.date_from = dateFrom.toISOString();
  if (dateTo) queryParams.date_to = endOfDay(dateTo).toISOString();

  const {
    data: summaryData,
    isLoading: summaryLoading,
    refetch: refetchSummary,
  } = useCustom<SummaryData>({
    url: `${apiUrl}/cancel_dashboard/summary`,
    method: "get",
    config: { query: queryParams },
  });

  const {
    data: reasonsData,
    isLoading: reasonsLoading,
    refetch: refetchReasons,
  } = useCustom<ReasonBreakdownItem[]>({
    url: `${apiUrl}/cancel_dashboard/reasons_breakdown`,
    method: "get",
    config: { query: queryParams },
  });

  const {
    data: offersData,
    isLoading: offersLoading,
    refetch: refetchOffers,
  } = useCustom<OffersBreakdownData>({
    url: `${apiUrl}/cancel_dashboard/offers_breakdown`,
    method: "get",
    config: { query: queryParams },
  });

  const {
    data: watchlistData,
    isLoading: watchlistLoading,
    refetch: refetchWatchlist,
  } = useCustom<WatchlistSummaryData>({
    url: `${apiUrl}/cancel_dashboard/watchlist_summary`,
    method: "get",
    config: { query: queryParams },
  });

  const {
    data: savePerformanceData,
    isLoading: savePerformanceLoading,
    refetch: refetchSavePerformance,
  } = useCustom<SavePerformanceData>({
    url: `${apiUrl}/cancel_dashboard/save_performance`,
    method: "get",
    config: { query: queryParams },
  });

  const {
    data: watchlistPeopleData,
    isLoading: watchlistPeopleLoading,
    refetch: refetchWatchlistPeople,
  } = useList<CancelPerson>({
    resource: "cancel_people",
    filters: [
      { field: "tab", operator: "eq", value: "watchlist" },
      ...(brand
        ? [{ field: "brand", operator: "eq" as const, value: brand }]
        : []),
      ...(dateFrom
        ? [
            {
              field: "date_from",
              operator: "eq" as const,
              value: dateFrom.toISOString(),
            },
          ]
        : []),
      ...(dateTo
        ? [
            {
              field: "date_to",
              operator: "eq" as const,
              value: endOfDay(dateTo).toISOString(),
            },
          ]
        : []),
    ],
    sorters: [{ field: "started_at", order: "desc" }],
    pagination: { pageSize: 100 },
  });

  // Query strings carrying the active dashboard filters to the detail
  // screens, so they open showing the same data. Param names differ per
  // destination: Cancel Sessions uses date_start/date_end, People uses
  // date_from/date_to.
  const sessionsSearch = (outcome?: string) => {
    const params = new URLSearchParams();
    if (brand) params.set("brand", brand);
    if (dateFrom) params.set("date_start", dateFrom.toISOString());
    if (dateTo) params.set("date_end", dateTo.toISOString());
    if (outcome) params.set("outcome", outcome);
    const qs = params.toString();
    return `/cancel_sessions${qs ? `?${qs}` : ""}`;
  };
  const peopleSearch = (tab: string) => {
    const params = new URLSearchParams({ tab });
    if (brand) params.set("brand", brand);
    if (dateFrom) params.set("date_from", dateFrom.toISOString());
    if (dateTo) params.set("date_to", dateTo.toISOString());
    return `/cancel_people?${params.toString()}`;
  };

  const summary = summaryData?.data;
  const reasons = reasonsData?.data;
  const offers = offersData?.data;
  const watchlist = watchlistData?.data;
  const savePerformance = savePerformanceData?.data;
  const watchlistPeople = useMemo(() => {
    const people = watchlistPeopleData?.data ?? [];
    return people.filter((person) => {
      const startedAt = new Date(person.started_at);
      if (dateFrom && startedAt < dateFrom) return false;
      if (dateTo && startedAt > endOfDay(dateTo)) return false;
      return true;
    });
  }, [watchlistPeopleData?.data, dateFrom, dateTo]);

  const totalReasons = reasons
    ? (reasons as unknown as ReasonBreakdownItem[]).reduce(
        (sum: number, r: ReasonBreakdownItem) => sum + r.count,
        0,
      )
    : 0;

  const isLoading =
    summaryLoading ||
    reasonsLoading ||
    offersLoading ||
    watchlistLoading ||
    savePerformanceLoading;

  const refetchAll = () => {
    refetchSummary();
    refetchReasons();
    refetchOffers();
    refetchWatchlist();
    refetchSavePerformance();
    refetchWatchlistPeople();
  };

  const handleResolveWatchlist = () => {
    openConfirmModal({
      title: "Resolve Watchlist Entries",
      children: (
        <Text size="sm">
          This will immediately trigger the watchlist resolution worker. Unlike
          the scheduled daily job (which only resolves overdue entries 30+ days
          old), this manual trigger will resolve <b>all</b> watching entries.
          Each entry will be checked against ChargeBee and marked as{" "}
          <b>saved</b> (subscription still active) or <b>lost</b> (subscription
          cancelled/non-renewing). Are you sure you want to proceed?
        </Text>
      ),
      labels: { confirm: "Resolve Watchlist", cancel: "Cancel" },
      confirmProps: { color: "blue" },
      onConfirm: async () => {
        setResolvingWatchlist(true);
        try {
          await axiosInstance.post(
            `${apiUrl}/cancel_dashboard/resolve_watchlist`,
          );
          refetchAll();
        } catch {
          // error handled by axios interceptor
        } finally {
          setResolvingWatchlist(false);
        }
      },
    });
  };

  const handleResolveSessions = () => {
    openConfirmModal({
      title: "Resolve Stale Sessions",
      children: (
        <Text size="sm">
          This will immediately trigger the session resolution worker. Unlike
          the scheduled daily job (which only resolves sessions older than 24
          hours), this manual trigger will resolve <b>all</b> in-progress cancel
          sessions. Each session will be checked against ChargeBee — if the
          subscription was cancelled it will be marked as <b>cancelled</b>,
          otherwise it will be marked as <b>abandoned</b> and a watchlist entry
          will be created to monitor the customer. Are you sure you want to
          proceed?
        </Text>
      ),
      labels: { confirm: "Resolve Sessions", cancel: "Cancel" },
      confirmProps: { color: "blue" },
      onConfirm: async () => {
        setResolvingSessions(true);
        try {
          await axiosInstance.post(
            `${apiUrl}/cancel_dashboard/resolve_sessions`,
          );
          refetchAll();
        } catch {
          // error handled by axios interceptor
        } finally {
          setResolvingSessions(false);
        }
      },
    });
  };

  return (
    <Flex direction="column" gap="lg">
      <Group position="apart">
        <Title order={3}>Cancel Flow Dashboard</Title>
        <Group>
          <Select
            placeholder="All Brands"
            data={[{ value: "", label: "All Brands" }, ...selectBrands]}
            value={brand}
            onChange={setBrand}
            clearable
            sx={{ width: 180 }}
          />
          <DatePicker
            placeholder="From"
            value={dateFrom}
            onChange={setDateFrom}
            clearable
            sx={{ width: 160 }}
          />
          <DatePicker
            placeholder="To"
            value={dateTo}
            onChange={setDateTo}
            clearable
            sx={{ width: 160 }}
          />
          <ActionIcon variant="subtle" onClick={refetchAll} loading={isLoading}>
            <IconRefresh size={18} />
          </ActionIcon>
        </Group>
      </Group>

      {isLoading && (
        <Flex justify="center" p="xl">
          <Loader />
        </Flex>
      )}

      {/* Summary Cards */}
      {summary && (
        <Grid>
          <Grid.Col span={2}>
            <StatCard
              title="Total Sessions"
              value={summary.total}
              color="blue"
              to={sessionsSearch()}
            />
          </Grid.Col>
          <Grid.Col span={2}>
            <StatCard
              title="Retained Revenue"
              value={`$${(offers?.by_offer?.reduce((sum: number, item: OfferBreakdownItem) => sum + item.saved_revenue, 0) ?? 0).toLocaleString()}`}
              color="teal"
              subtitle="From accepted offers"
            />
          </Grid.Col>
          <Grid.Col span={2}>
            <Paper p="md" radius="md" withBorder sx={{ height: "100%" }}>
              <Text size="xs" color="dimmed" tt="uppercase" fw={700}>
                Deflection Rate
              </Text>
              <Flex align="center" gap="md" mt={4}>
                <RingProgress
                  size={60}
                  thickness={6}
                  roundCaps
                  sections={[
                    {
                      value: summary.deflection_rate,
                      color: "green",
                    },
                  ]}
                  label={
                    <Text size="xs" align="center" fw={700}>
                      {summary.deflection_rate}%
                    </Text>
                  }
                />
                <Text size="xl" fw={700} color="green">
                  {summary.deflection_rate}%
                </Text>
              </Flex>
            </Paper>
          </Grid.Col>
          <Grid.Col span={2}>
            <StatCard
              title="Deflected"
              value={summary.deflected}
              color="green"
              subtitle="Saved from cancelling"
              to={peopleSearch("saves")}
            />
          </Grid.Col>
          <Grid.Col span={2}>
            <StatCard
              title="Cancelled"
              value={summary.cancelled}
              color="red"
              to={peopleSearch("cancels")}
            />
          </Grid.Col>
          <Grid.Col span={2}>
            <StatCard
              title="Abandoned"
              value={summary.abandoned}
              color="gray"
              subtitle="Left without completing"
              to={sessionsSearch("abandoned")}
            />
          </Grid.Col>
          <Grid.Col span={2}>
            <StatCard
              title="In Progress"
              value={summary.in_progress}
              color="blue"
              subtitle="Currently active"
              to={sessionsSearch("in_progress")}
            />
          </Grid.Col>
        </Grid>
      )}

      {/* Save Performance Chart */}
      {savePerformance &&
        savePerformance.data &&
        savePerformance.data.length > 0 && (
          <Paper p="md" radius="md" withBorder>
            <Group position="apart" mb="md">
              <div>
                <Group spacing={6}>
                  <Title order={5}>Save performance</Title>
                  <Tooltip
                    label="Deflect rate = sessions deflected / total sessions. Save rate = watchlist entries confirmed saved / total sessions. Both measured by session start date."
                    multiline
                    width={320}
                    withArrow
                    position="top"
                  >
                    <ActionIcon size="xs" variant="transparent" color="gray">
                      <IconInfoCircle size={16} />
                    </ActionIcon>
                  </Tooltip>
                </Group>
                <Text size="xs" color="dimmed">
                  by session start date
                </Text>
              </div>
              <Group spacing="xl">
                <div>
                  <Text size="xl" fw={700} color="teal" component="span">
                    {savePerformance.overall_deflect_rate}%
                  </Text>
                  <Text size="xs" color="dimmed" ml={4} component="span">
                    Deflect rate
                  </Text>
                </div>
                <div>
                  <Text size="xl" fw={700} color="green" component="span">
                    {savePerformance.overall_save_rate}%
                  </Text>
                  <Text size="xs" color="dimmed" ml={4} component="span">
                    Save rate
                  </Text>
                </div>
              </Group>
            </Group>
            <ResponsiveContainer width="100%" height={280}>
              <LineChart data={savePerformance.data}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                <XAxis
                  dataKey="week"
                  tickFormatter={(v: string) => {
                    const d = new Date(v);
                    return `${d.getMonth() + 1}/${d.getDate()}`;
                  }}
                  fontSize={12}
                />
                <YAxis
                  tickFormatter={(v: number) => `${v}%`}
                  fontSize={12}
                  width={45}
                />
                <RechartsTooltip
                  formatter={(value: any, name: any) => [
                    `${value}%`,
                    name === "deflect_rate" ? "Deflect rate" : "Save rate",
                  ]}
                  labelFormatter={(label: any) => {
                    const d = new Date(String(label));
                    return `Week of ${d.toLocaleDateString()}`;
                  }}
                />
                <Legend
                  formatter={(value: string) =>
                    value === "deflect_rate" ? "Deflect rate" : "Save rate"
                  }
                />
                <Line
                  type="monotone"
                  dataKey="deflect_rate"
                  stroke="#20c997"
                  strokeWidth={2}
                  dot={{ r: 3 }}
                  activeDot={{ r: 5 }}
                />
                <Line
                  type="monotone"
                  dataKey="save_rate"
                  stroke="#51cf66"
                  strokeWidth={2}
                  dot={{ r: 3 }}
                  activeDot={{ r: 5 }}
                />
              </LineChart>
            </ResponsiveContainer>
          </Paper>
        )}

      {/* Deflection Funnel + Active Watchlist */}
      <Grid>
        <Grid.Col span={8}>
          {summary && summary.total > 0 && (
            <Paper p="md" radius="md" withBorder sx={{ height: "100%" }}>
              <Group position="apart" mb="md">
                <div>
                  <Title order={5}>Deflection Funnel</Title>
                  <Text size="xs" color="dimmed">
                    by session start date
                  </Text>
                </div>
                <Text size="sm" fw={700}>
                  Deflection rate{" "}
                  <Text component="span" fw={700} size="lg" color="green">
                    {summary.deflection_rate}%
                  </Text>
                </Text>
              </Group>

              {(() => {
                const saves = watchlist?.saved ?? 0;
                const watching = watchlist?.watching ?? 0;
                const lost = watchlist?.lost ?? 0;
                const cancelled = summary.cancelled;
                const deflectedCount = saves + watching + lost;
                const bottomTotal = deflectedCount + cancelled;
                const deflectedPct =
                  bottomTotal > 0
                    ? (deflectedCount / bottomTotal) * 100
                    : summary.deflection_rate;

                const segments = [
                  {
                    label: "Saves",
                    count: saves,
                    color: "#51cf66",
                    tooltip: `${saves.toLocaleString()} Saves — ${deflectedCount > 0 ? ((saves / deflectedCount) * 100).toFixed(1) : 0}% of deflects have been Saved`,
                  },
                  {
                    label: "Watch list",
                    count: watching,
                    color: "#fcc419",
                    tooltip: `${watching.toLocaleString()} Watch list — ${deflectedCount > 0 ? ((watching / deflectedCount) * 100).toFixed(1) : 0}% of deflects are still on the Watch list`,
                  },
                  {
                    label: "Lost deflects",
                    count: lost,
                    color: "#ff8787",
                    tooltip: `${lost.toLocaleString()} Lost deflects — ${deflectedCount > 0 ? ((lost / deflectedCount) * 100).toFixed(1) : 0}% who initially deflected later returned and canceled`,
                  },
                  {
                    label: "Cancels",
                    count: cancelled,
                    color: "#ffc9c9",
                    tooltip: `${cancelled.toLocaleString()} Cancels — ${summary.total > 0 ? ((cancelled / summary.total) * 100).toFixed(1) : 0}% of unique subscriber sessions canceled immediately`,
                  },
                ].filter((s) => s.count > 0);

                const pct = Math.max(deflectedPct, 8);

                return (
                  <Flex direction="column">
                    {/* Top bar — Total sessions */}
                    <Text size="xs" color="dimmed" align="center" mb={4}>
                      Unique subscriber sessions
                    </Text>
                    <Tooltip
                      label={`${summary.total.toLocaleString()} unique subscriber sessions`}
                      position="bottom"
                      withArrow
                    >
                      <div
                        style={{
                          background: "#4dabf7",
                          borderRadius: 6,
                          height: 40,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          cursor: "pointer",
                        }}
                        onClick={() => navigate(sessionsSearch())}
                      >
                        <Text color="white" fw={700} size="sm">
                          {summary.total.toLocaleString()}
                        </Text>
                      </div>
                    </Tooltip>

                    {/* Guide lines + Deflection rate bar wrapper */}
                    {(() => {
                      // Compute segment centers for guide lines
                      const segCenters: { pos: number; label: string }[] = [];
                      let cumulative = 0;
                      for (const seg of segments) {
                        const segPct = (seg.count / bottomTotal) * 100;
                        segCenters.push({
                          pos: cumulative + segPct / 2,
                          label: seg.label,
                        });
                        cumulative += segPct;
                      }
                      // Heights: top gap 16px, deflection bar 32px, bottom gap 16px = 64px total
                      return (
                        <div style={{ position: "relative", height: 64 }}>
                          {/* Deflection rate bar */}
                          <Tooltip
                            label={`${summary.deflected.toLocaleString()} Deflects — ${summary.deflection_rate}% of unique subscriber sessions deflected from canceling`}
                            position="bottom"
                            withArrow
                            multiline
                            width={280}
                          >
                            <div
                              style={{
                                position: "absolute",
                                top: 16,
                                left: 0,
                                width: `${pct}%`,
                                background: "#1c7ed6",
                                borderRadius: 6,
                                height: 32,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                cursor: "default",
                                zIndex: 1,
                              }}
                            >
                              <Text color="white" fw={700} size="xs">
                                {summary.deflection_rate}%
                              </Text>
                            </div>
                          </Tooltip>

                          {bottomTotal > 0 &&
                            segCenters.map((seg, i) => {
                              if (seg.label === "Cancels") {
                                // Cancels → full height (connects bottom segments to top bar)
                                return (
                                  <div
                                    key={i}
                                    style={{
                                      position: "absolute",
                                      left: `${seg.pos}%`,
                                      top: 0,
                                      bottom: 0,
                                      borderLeft: "1px solid #ced4da",
                                    }}
                                  />
                                );
                              }
                              // Saves, Watch list, Lost deflects → bottom gap only (bottom segments to deflection bar)
                              return (
                                <div
                                  key={i}
                                  style={{
                                    position: "absolute",
                                    left: `${seg.pos}%`,
                                    top: 48,
                                    bottom: 0,
                                    borderLeft: "1px solid #ced4da",
                                  }}
                                />
                              );
                            })}
                          {/* Deflection bar center → top gap (deflection bar to top bar) */}
                          {bottomTotal > 0 && (
                            <div
                              style={{
                                position: "absolute",
                                left: `${pct / 2}%`,
                                top: 0,
                                height: 16,
                                borderLeft: "1px solid #ced4da",
                              }}
                            />
                          )}
                        </div>
                      );
                    })()}

                    {/* Bottom row — Stacked segments */}
                    {bottomTotal > 0 && (
                      <>
                        <Flex
                          style={{
                            borderRadius: 6,
                            overflow: "hidden",
                            height: 32,
                          }}
                        >
                          {segments.map((seg) => (
                            <Tooltip
                              key={seg.label}
                              label={seg.tooltip}
                              position="bottom"
                              withArrow
                              multiline
                              width={280}
                            >
                              <div
                                style={{
                                  width: `${(seg.count / bottomTotal) * 100}%`,
                                  background: seg.color,
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  minWidth: 40,
                                  cursor: "default",
                                }}
                              >
                                <Text
                                  fw={700}
                                  size="xs"
                                  color={
                                    seg.label === "Cancels" ? "dark" : "white"
                                  }
                                  style={{ whiteSpace: "nowrap" }}
                                >
                                  {seg.count.toLocaleString()}
                                </Text>
                              </div>
                            </Tooltip>
                          ))}
                        </Flex>
                        <Flex>
                          {segments.map((seg) => (
                            <Text
                              key={seg.label}
                              size="xs"
                              color="dimmed"
                              align="center"
                              mt={4}
                              style={{
                                width: `${(seg.count / bottomTotal) * 100}%`,
                              }}
                            >
                              {seg.label}
                            </Text>
                          ))}
                        </Flex>
                      </>
                    )}
                  </Flex>
                );
              })()}
            </Paper>
          )}
        </Grid.Col>
        <Grid.Col span={4}>
          <Paper p="md" radius="md" withBorder sx={{ height: "100%" }}>
            <Title order={5} mb="md">
              Offer Performance
            </Title>
            {offers ? (
              <Flex direction="column" gap="md">
                <Grid>
                  <Grid.Col span={4}>
                    <Text size="xs" color="dimmed" tt="uppercase" fw={700}>
                      Total Shown
                    </Text>
                    <Text size="lg" fw={700}>
                      {offers.total_shown}
                    </Text>
                  </Grid.Col>
                  <Grid.Col span={4}>
                    <Text size="xs" color="dimmed" tt="uppercase" fw={700}>
                      Total Accepted
                    </Text>
                    <Text size="lg" fw={700} color="green">
                      {offers.total_accepted}
                    </Text>
                  </Grid.Col>
                  <Grid.Col span={4}>
                    <Text size="xs" color="dimmed" tt="uppercase" fw={700}>
                      Acceptance Rate
                    </Text>
                    <Flex align="center" gap="xs">
                      <RingProgress
                        size={50}
                        thickness={5}
                        roundCaps
                        sections={[
                          { value: offers.acceptance_rate, color: "teal" },
                        ]}
                      />
                      <Text size="lg" fw={700} color="teal">
                        {offers.acceptance_rate}%
                      </Text>
                    </Flex>
                  </Grid.Col>
                </Grid>

                {offers.by_type && offers.by_type.length > 0 && (
                  <Table>
                    <thead>
                      <tr>
                        <th>Offer Type</th>
                        <th>Shown</th>
                        <th>Accepted</th>
                        <th>Rate</th>
                      </tr>
                    </thead>
                    <tbody>
                      {offers.by_type.map((item) => (
                        <tr key={item.offer_type}>
                          <td>
                            <Badge
                              color={
                                colorsByOfferType[
                                  item.offer_type as keyof typeof colorsByOfferType
                                ] || "gray"
                              }
                            >
                              {item.offer_type}
                            </Badge>
                          </td>
                          <td>
                            <Text size="sm">{item.shown}</Text>
                          </td>
                          <td>
                            <Text size="sm" fw={700} color="green">
                              {item.accepted}
                            </Text>
                          </td>
                          <td>
                            <Text size="sm">
                              {item.shown > 0
                                ? ((item.accepted / item.shown) * 100).toFixed(
                                    1,
                                  )
                                : 0}
                              %
                            </Text>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                )}
              </Flex>
            ) : (
              <Text color="dimmed" size="sm">
                No offers data
              </Text>
            )}
          </Paper>
        </Grid.Col>
      </Grid>

      {/* Watchlist Summary */}
      {watchlist && (
        <Paper p="md" radius="md" withBorder>
          <Group position="apart" mb="md">
            <Title order={5}>Watchlist Summary</Title>
            <Group spacing="xs">
              <Button
                variant="light"
                color="blue"
                size="xs"
                loading={resolvingWatchlist}
                onClick={handleResolveWatchlist}
              >
                Resolve Watchlist
              </Button>
              <Button
                variant="light"
                color="gray"
                size="xs"
                loading={resolvingSessions}
                onClick={handleResolveSessions}
              >
                Resolve Sessions
              </Button>
            </Group>
          </Group>
          <Grid>
            <Grid.Col span={3}>
              <StatCard
                title="Watching"
                value={watchlist.watching}
                color="blue"
                subtitle="Being monitored"
                to={peopleSearch("watchlist")}
              />
            </Grid.Col>
            <Grid.Col span={3}>
              <StatCard
                title="Saved"
                value={watchlist.saved}
                color="green"
                subtitle="Successfully retained"
              />
            </Grid.Col>
            <Grid.Col span={3}>
              <StatCard
                title="Lost"
                value={watchlist.lost}
                color="red"
                subtitle="Eventually cancelled"
              />
            </Grid.Col>
            <Grid.Col span={3}>
              <StatCard
                title="Overdue"
                value={watchlist.overdue}
                color="orange"
                subtitle="Needs attention"
              />
            </Grid.Col>
          </Grid>
        </Paper>
      )}

      <Grid>
        {/* Reasons Breakdown */}
        <Grid.Col span={6}>
          <Paper p="md" radius="md" withBorder sx={{ height: "100%" }}>
            <Title order={5} mb="md">
              Cancel Reasons
            </Title>
            {reasons &&
            (reasons as unknown as ReasonBreakdownItem[]).length > 0 ? (
              <CancelReasonsPieChart
                reasons={reasons as unknown as ReasonBreakdownItem[]}
                totalReasons={totalReasons}
                onReasonClick={() => navigate("/cancel_reports")}
              />
            ) : (
              <Text color="dimmed" size="sm">
                No cancel reasons data
              </Text>
            )}
          </Paper>
        </Grid.Col>

        {/* Active Watchlist */}
        <Grid.Col span={6}>
          <Paper
            p="md"
            radius="md"
            withBorder
            sx={{ height: "100%", display: "flex", flexDirection: "column" }}
          >
            <Group spacing={6} mb={4}>
              <Title order={5}>Active watch list</Title>
              <Tooltip
                label="All customers that visit the Cancel Page but do not continue to Cancel (Pending/Abandon/Deflect) should be put on Watchlist for 30 days, after 30 days they are marked as Saved (not cancel, renew) or goes to Lost Deflect (they cancel before 30 days)."
                multiline
                width={320}
                withArrow
                position="top"
              >
                <ActionIcon size="xs" variant="transparent" color="gray">
                  <IconInfoCircle size={16} />
                </ActionIcon>
              </Tooltip>
            </Group>
            <Text size="xs" color="dimmed" mb="md">
              by highest value
            </Text>
            {watchlist && (
              <Group mb="md" spacing="xl">
                <div>
                  <Text size="xs" color="dimmed" tt="uppercase" fw={700}>
                    Value at risk
                  </Text>
                  <Text size="lg" fw={700} color="red">
                    $
                    {watchlistPeople
                      .reduce(
                        (sum, p) =>
                          sum + (parseFloat(p.subscription_mrr ?? "0") || 0),
                        0,
                      )
                      .toLocaleString()}
                  </Text>
                </div>
                <div>
                  <Text size="xs" color="dimmed" tt="uppercase" fw={700}>
                    Customers
                  </Text>
                  <Text size="lg" fw={700}>
                    {watchlist.watching}
                  </Text>
                </div>
              </Group>
            )}
            {watchlistPeople.length > 0 ? (
              <Table fontSize="xs" striped>
                <thead>
                  <tr>
                    <th>Customer</th>
                    <th>Return likelihood</th>
                    <th>Value</th>
                    <th>Reason</th>
                  </tr>
                </thead>
                <tbody>
                  {watchlistPeople.slice(0, 10).map((person) => (
                    <tr key={person.id}>
                      <td>
                        {person.user
                          ? [person.user.first_name, person.user.last_name]
                              .filter(Boolean)
                              .join(" ") || person.user.email
                          : person.chargebee_customer_id}
                      </td>
                      <td>
                        {person.cancel_survey_response?.return_likelihood ??
                          "—"}
                      </td>
                      <td>
                        $
                        {parseFloat(
                          person.subscription_mrr ?? "0",
                        ).toLocaleString()}
                      </td>
                      <td>
                        {person.cancel_survey_response?.cancellation_reason
                          ?.label ?? "—"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            ) : !watchlistPeopleLoading ? (
              <Text color="dimmed" size="sm">
                No customers on watch list
              </Text>
            ) : null}
            <Anchor
              component={Link}
              to={`/cancel_people?tab=watchlist${brand ? `&brand=${brand}` : ""}${dateFrom ? `&date_from=${dateFrom.toISOString()}` : ""}${dateTo ? `&date_to=${dateTo.toISOString()}` : ""}`}
              size="xs"
              mt="auto"
              pt="md"
              sx={{ display: "block" }}
            >
              View customers on watch list →
            </Anchor>
          </Paper>
        </Grid.Col>
      </Grid>
    </Flex>
  );
};
